/* import __COLOCATED_TEMPLATE__ from './editor.hbs'; */
/* RESPONSIBLE TEAM: team-phone */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import type IntlService from 'ember-intl/services/intl';
import type { Block } from '@intercom/interblocks.ts';
import type PhoneOneTimePasscode from 'embercom/models/operator/visual-builder/step/phone-one-time-passcode';
import type EditorState from 'embercom/objects/workflows/graph-editor/editor-state';

interface Signature {
  Args: {
    step: PhoneOneTimePasscode;
    isReadOnly: boolean;
    editorState: EditorState;
    className?: string;
  };
  Element: HTMLDivElement;
}

export default class PhoneOneTimePasscodeEditor extends Component<Signature> {
  @service attributeService: any;
  @service intl!: IntlService;
  @service appService: any;
  @tracked selectedTTSMode: string;
  @tracked selectedLocale: string;
  @service store: any;

  ttsModes = [
    {
      text: this.intl.t(
        'operator.workflows.visual-builder.hold-and-assign-editor.exit-tab.audio-snippet-editor.tts-mode',
      ),
      value: 'Text to Speech',
    },
    {
      text: this.intl.t(
        'operator.workflows.visual-builder.hold-and-assign-editor.exit-tab.audio-snippet-editor.audio-snippet-mode',
      ),
      value: 'Audio Snippets',
    },
  ];

  locales = [
    {
      text: this.intl.t(
        'operator.workflows.visual-builder.hold-and-assign-editor.exit-tab.audio-snippet-editor.usa-english',
      ),
      value: 'en-US',
    },
    {
      text: this.intl.t(
        'operator.workflows.visual-builder.hold-and-assign-editor.exit-tab.audio-snippet-editor.uk-english',
      ),
      value: 'en-GB',
    },
    {
      text: this.intl.t(
        'operator.workflows.visual-builder.hold-and-assign-editor.exit-tab.audio-snippet-editor.french',
      ),
      value: 'fr-FR',
    },
    {
      text: this.intl.t(
        'operator.workflows.visual-builder.hold-and-assign-editor.exit-tab.audio-snippet-editor.german',
      ),
      value: 'de-DE',
    },
    {
      text: this.intl.t(
        'operator.workflows.visual-builder.hold-and-assign-editor.exit-tab.audio-snippet-editor.spanish',
      ),
      value: 'es-ES',
    },
    {
      text: this.intl.t(
        'operator.workflows.visual-builder.hold-and-assign-editor.exit-tab.audio-snippet-editor.portuguese',
      ),
      value: 'pt-PT',
    },
    {
      text: this.intl.t(
        'operator.workflows.visual-builder.hold-and-assign-editor.exit-tab.audio-snippet-editor.br-portuguese',
      ),
      value: 'pt-BR',
    },
  ];

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);

    this.selectedTTSMode = this.args.step.ttsMode || this.ttsModes[0].value;
    this.selectedLocale = this.args.step.ttsLanguage || this.locales[0].value;
  }

  get app() {
    return this.appService.app;
  }

  get blocks(): Array<Block> {
    // Convert FragmentArray to array if needed, always read from step.blocks
    if (!this.args.step.blocks) {
      return [];
    }
    if (this.args.step.blocks.toArray) {
      return this.args.step.blocks.toArray();
    }
    if (Array.isArray(this.args.step.blocks)) {
      return this.args.step.blocks;
    }
    return [];
  }

  @action
  updateTTSMode(ttsMode: string) {
    (this.args.step.blocks as any) = new Array<Block>();
    this.selectedTTSMode = ttsMode;
    this.args.step.ttsMode = ttsMode;
    this.args.step.notifyPropertyChange('blocks');
  }

  @action
  updateLocale(locale: string) {
    this.selectedLocale = locale;
    this.args.step.ttsLanguage = locale;
  }

  @action
  updateBlocks(blocksDoc: Array<Block>) {
    (this.args.step.blocks as any) = blocksDoc;
    this.args.step.notifyPropertyChange('blocks');
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::GraphEditor::NodeItems::Steps::PhoneOneTimePasscode::Editor': typeof PhoneOneTimePasscodeEditor;
    'workflows/graph-editor/node-items/steps/phone-one-time-passcode/editor': typeof PhoneOneTimePasscodeEditor;
  }
}
